// The shape of the synthetic case (lib/mock-*.ts): the WEG Musterstraße 12 with two comparisons, three
// contracts and three inbox documents. Every name is invented (Muster, Beispiel, Probe); no figure here is a
// real price. Screens mark the case with the sample tag and the source line, never as the user's own data.
// Offers stay in their given order: the product never ranks.
//
// One module per screen keeps the entry small: the home route carries only its tasks, each code-split route
// its own data. Money is integer cents; the yearly total of the contracts adds brutto years only. Dates are
// ISO days counted from MOCK_TODAY, so the relative labels ("in 9 Tagen") match on every visit.

import type { GlyphName } from "../components/Glyph";
import type { MessageId } from "./i18n";

/** The day the mock case counts from: relative labels stay coherent no matter when the page opens. */
export const MOCK_TODAY = "2026-10-04";

export type CaseTarget =
  | { kind: "vergleich"; vergleichId: string }
  | { kind: "vertrag"; vertragId: string }
  | { kind: "kosten" };

export interface TaskSegment {
  text: string;
  strong: boolean;
}

export type TaskAction = "deadline" | "answer" | "letter";

export interface HeuteTask {
  id: string;
  dateISO: string;
  glyph: GlyphName;
  title: string;
  meta: string;
  then: ReadonlyArray<TaskSegment>;
  action: TaskAction;
  target: CaseTarget;
}

export interface MockOffer {
  name: string;
  /** Brutto total in cents, or null when the offer states no total ("Nicht angegeben", never zero). */
  totalCents: number | null;
}

export interface MockVergleich {
  id: string;
  title: string;
  site: string;
  requirementsTotal: number;
  requirementsOpen: number;
  offers: ReadonlyArray<MockOffer>;
  questionsAnswered: number;
  questionsOpen: number;
  updatedISO: string;
}

export interface MockFact {
  label: MessageId;
  /** Null stays "Nicht angegeben" on screen (the message, never zero, never "nicht enthalten"). */
  value: string | null;
  method: string | null;
}

export interface MockVertrag {
  id: string;
  service: string;
  supplier: string;
  /** Brutto year in cents, or null when no amount is known (the total then stays a floor). */
  yearlyCents: number | null;
  nextDateISO: string;
  nextEvent: string;
  consequence: string;
  /** The price clause in one line, or null when the contract does not state one. */
  clause: string | null;
  /** True while a price letter waits for an answer (the row carries a "Preisschreiben offen" chip). */
  letterOpen: boolean;
  facts: ReadonlyArray<MockFact>;
}

export type InboxKind = "offer" | "letter" | "contract";

export interface InboxItem {
  id: string;
  receivedISO: string;
  kind: InboxKind;
  title: string;
  meta: string;
  target: CaseTarget;
}

export const mockSite = {
  name: "WEG Musterstraße 12",
  place: "12345 Beispielstadt",
  units: 14,
};
